// src/components/Navbar/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import rdvWhite from '../../assets/rdv-logoW.png';
import rdvBlack from '../../assets/rdv-logoB.png';
import { useFileContext } from '../../ContextAPI/FileContext';
import AppInstall from '../AppInstall/AppInstall';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const { auth, logout } = useFileContext();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  // White logo on the home page (dark hero + dark glass navbar); black logo on
  // the white-navbar marketing pages.
  const logoSrc = isHomePage ? rdvWhite : rdvBlack;

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the drawer whenever the route changes (link tap navigates away).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // While the drawer is open: lock body scroll, close on Escape, and flag
  // the body so the open menu becomes the top layer (hides the fixed
  // accessibility toggle that otherwise floats over the drawer).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('rsa-mobile-menu-open');
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('rsa-mobile-menu-open');
    };
  }, [menuOpen]);

  const openAccount = () => {
    setMenuOpen(false);
    navigate('/account');
  };

  // Account (settings) affordance — only for signed-in users.
  const renderAccountAction = () =>
    auth ? (
      <button className="login-btn account-btn" onClick={openAccount}>
        <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 11c-1.66 0-3 1.34-3 3v3" />
          <path d="M7.5 8.5a5 5 0 0 1 9 3v2" />
          <path d="M12 15v3" />
        </svg>
        Account
      </button>
    ) : null;

  // Login / logout affordance — reused inline (desktop) and in the drawer.
  const renderLoginAction = () =>
    auth ? (
      <button className="login-btn" onClick={logout}>
        <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        Logout
      </button>
    ) : (
      <Link to="/login" className="login-btn">
        <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        Login
      </Link>
    );

  return (
    <>
      <div className={`Navbar_Container ${isHomePage ? 'transparent' : 'white'}`}>
        <nav className={`navbar ${isHomePage ? 'transparent' : 'white'} ${isScrolled ? 'scrolled' : ''}`}>
          <div className='navbar-logo'>
            <img className='rdvImageNav' src={logoSrc} alt='rdv' />
          </div>

          {/* Desktop inline links (hidden on mobile) */}
          <div className='navbar-links'>
            {NAV_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className="navbar-link">{link.label}</Link>
            ))}
          </div>

          {/* Desktop login (hidden on mobile) */}
          <div className='navbar-login'>
            {renderAccountAction()}
            {renderLoginAction()}
          </div>

          {/* Mobile hamburger (hidden on desktop) */}
          <button
            type="button"
            className={`navbar-hamburger ${menuOpen ? 'open' : ''}`}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-drawer"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </nav>
      </div>

      {/* Mobile drawer (the transparent navbar, collapsed behind the hamburger) */}
      <div
        className={`mobile-drawer-backdrop ${menuOpen ? 'show' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <aside
        id="mobile-nav-drawer"
        className={`mobile-drawer ${menuOpen ? 'open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav className="mobile-drawer-links" aria-label="Main navigation">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="mobile-drawer-link"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="mobile-drawer-footer">
          <div className="mobile-drawer-login">
            {renderAccountAction()}
            {renderLoginAction()}
          </div>
          <AppInstall />
        </div>
      </aside>
    </>
  );
};

export default Navbar;
